<template>
  <div class="wizard-body small-9 columns">
    <page-header
      :header-title="$t('INBOX_MGMT.ADD.ZENDESK_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.ZENDESK_CHANNEL.DESC')"
    />
    <form class="row" @submit.prevent="createChannel()">
      <div class="medium-8 columns">
        <label :class="{ error: $v.channelName.$error }">
          {{ $t('INBOX_MGMT.ADD.ZENDESK_CHANNEL.CHANNEL_NAME.LABEL') }}
          <input
            v-model.trim="channelName"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.ZENDESK_CHANNEL.CHANNEL_NAME.PLACEHOLDER')
            "
            @blur="$v.channelName.$touch"
          />
          <span v-if="$v.channelName.$error" class="message">{{
              $t('INBOX_MGMT.ADD.ZENDESK_CHANNEL.CHANNEL_NAME.ERROR')
            }}</span>
        </label>
      </div>

      <div class="medium-8 columns">
        <label :class="{ error: $v.subdomain.$error }">
          {{ $t('INBOX_MGMT.ADD.ZENDESK_CHANNEL.SUBDOMAIN.LABEL') }}
          <input
            v-model.trim="subdomain"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.ZENDESK_CHANNEL.SUBDOMAIN.PLACEHOLDER')
            "
            @blur="getZendeskData"
          />
          <span v-if="$v.subdomain.$error" class="message">{{
              $t('INBOX_MGMT.ADD.ZENDESK_CHANNEL.SUBDOMAIN.ERROR')
            }}</span>
        </label>
      </div>

      <div class="medium-8 columns">
        <label :class="{ error: $v.email.$error }">
          {{ $t('INBOX_MGMT.ADD.ZENDESK_CHANNEL.EMAIL.LABEL') }}
          <input
            v-model.trim="email"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.ZENDESK_CHANNEL.EMAIL.PLACEHOLDER')
            "
            @blur="getZendeskData"
          />
        </label>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.ZENDESK_CHANNEL.EMAIL.SUBTITLE') }}
        </p>
      </div>

      <div class="medium-8 columns">
        <label :class="{ error: $v.api_token.$error }">
          {{ $t('INBOX_MGMT.ADD.ZENDESK_CHANNEL.API_TOKEN.LABEL') }}
          <input
            v-model.trim="api_token"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.ZENDESK_CHANNEL.API_TOKEN.PLACEHOLDER')
            "
            @blur="getZendeskData"
          />
        </label>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.ZENDESK_CHANNEL.API_TOKEN.SUBTITLE') }}
        </p>
      </div>

      <div class="columns medium-12">
        <button class="header-section back-button" @click.prevent="showCustomFilters = !showCustomFilters">
          Adicionar filtros
          <div class="chevron-icon__wrap">
            <fluent-icon v-if="showCustomFilters" icon="chevron-up" />
            <fluent-icon v-else icon="chevron-down" />
          </div>
        </button>
      </div>

      <div v-if="showCustomFilters" class="columns medium-12">
        <div class="medium-8 columns">
          <label>
            Canais
            <multiselect
              v-model="channels"
              selectedLabel="Selecionado"
              placeholder="Selecionar canal"
              label="name"
              track-by="id"
              :max-height="160"
              :option-height="104"
              :multiple="true"
              :allow-empty="true"
              :options="channelOptions"
              :searchable="true"
            />
          </label>
          <p class="help-text">
            Considerar armazenar apenas atendimentos desses canais. Deixe vazio caso queira armazenar todos.
          </p>
        </div>

        <div class="medium-8 columns">
          <label>
            Grupos
            <multiselect
              v-model="groups"
              selectedLabel="Selecionado"
              placeholder="Selecionar grupos"
              label="name"
              track-by="id"
              :max-height="160"
              :option-height="104"
              :multiple="true"
              :allow-empty="true"
              :options="groupOptions"
              :loading="zendeskLoadingGroups"
              :disabled="!showGroups"
              :searchable="true"
            >
              <span slot="noOptions">Nenhum grupo encontrado</span>
            </multiselect>
          </label>
          <p class="help-text">
            Considerar armazenar apenas atendimentos desses grupos. Deixe vazio caso queira armazenar todas.
          </p>
        </div>

        <div class="medium-8 columns">
          <label>
            Tags Excluídas
            <multiselect
              v-model="tags"
              selectedLabel="Selecionado"
              placeholder="Selecionar tags"
              label="name"
              track-by="name"
              :max-height="160"
              :option-height="104"
              :multiple="true"
              :allow-empty="true"
              :options="tagsOptions"
              :loading="zendeskLoadingTags"
              :disabled="!showTags"
              :searchable="true"
            >
              <span slot="noOptions">Nenhuma tag encontrada</span>
            </multiselect>
          </label>
          <p class="help-text">
            Considerar ignoras atendimentos com essas tags. Deixe vazio caso queira armazenar todas.
          </p>
        </div>

        <div class="medium-8 columns">
          <label>
            Usuários
            <multiselect
              v-model="users"
              selectedLabel="Selecionado"
              placeholder="Selecionar usuários"
              label="name"
              track-by="id"
              :max-height="160"
              :option-height="104"
              :multiple="true"
              :allow-empty="true"
              :options="userOptions"
              :loading="zendeskLoadingUsers"
              :disabled="!showUsers"
              :searchable="true"
            >
              <span slot="noOptions">Nenhum usuário encontrado</span>
            </multiselect>
          </label>
          <p class="help-text">
            Considerar armazenar apenas atendimentos desses usuários. Deixe vazio caso queira armazenar todas.
          </p>
        </div>

        <div class="medium-8 columns">
          <label>
            Usuários Excluídos
            <multiselect
              v-model="excluded_users"
              selectedLabel="Selecionado"
              placeholder="Selecionar usuários"
              label="name"
              track-by="id"
              :max-height="160"
              :option-height="104"
              :multiple="true"
              :allow-empty="true"
              :options="userOptions"
              :loading="zendeskLoadingUsers"
              :disabled="!showUsers"
              :searchable="true"
            >
              <span slot="noOptions">Nenhum usuário encontrado</span>
            </multiselect>
          </label>
          <p class="help-text">
            Desconsiderar armazenar atendimentos desses usuários. Deixe vazio caso queira armazenar todas.
          </p>
        </div>

        <div class="medium-8 columns">
          <label :class="{ error: $v.minMessages.$error }">
            Mínimo de Mensagens
            <input
              v-model.trim="minMessages"
              type="text"
              @blur="$v.minMessages.$touch"
            />
            <span v-if="$v.minMessages.$error" class="message">
              Informe uma quantidade valida
            </span>
          </label>
          <p class="help-text">
            Deve ter essa quantidade mínima de mensagens total para poder armazenar os atendimentos.
          </p>

          <label class="bot-switch">
            Contabilizar mensagens do bot
            <woot-switch v-model="countBotMessages" />
            <p class="help-text">
              Se o filtro aplicado de quantidade mínima de mensagens na conversa
              deve considerar as mensagens do bot
            </p>
          </label>

            <label :class="{ error: $v.customerMinMessages.$error }">
              Mínimo de Mensagens do Cliente
              <input
                v-model.trim="customerMinMessages"
                type="text"
                @blur="$v.customerMinMessages.$touch"
              />
              <span v-if="$v.customerMinMessages.$error" class="message">
              Informe uma quantidade valida
            </span>
            </label>
            <p class="help-text">
              Deve ter essa quantidade mínima de mensagens do cliente para poder armazenar os atendimentos.
            </p>

            <label :class="{ error: $v.operatorMinMessages.$error }">
              Mínimo de Mensagens do Atendente
              <input
                v-model.trim="operatorMinMessages"
                type="text"
                @blur="$v.operatorMinMessages.$touch"
              />
              <span v-if="$v.operatorMinMessages.$error" class="message">
              Informe uma quantidade valida
            </span>
            </label>
            <p class="help-text">
              Deve ter essa quantidade mínima de mensagens do atendente para poder armazenar os atendimentos.
            </p>
        </div>

      </div>

      <div class="divider">&nbsp;</div>

      <div class="medium-12 columns">
        <woot-submit-button
          :loading="uiFlags.isCreating"
          :button-text="$t('INBOX_MGMT.ADD.ZENDESK_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>

<script>
import { required, url, numeric } from 'vuelidate/lib/validators';
import PageHeader from '../../SettingsSubPageHeader';
import alertMixin from 'shared/mixins/alertMixin';
import getUuid from 'widget/helpers/uuid';
import router from "../../../../index";
import { mapGetters } from 'vuex';

export default {
  name: 'ZendeskChannel',
  components: {
    PageHeader
  },
  mixins: [ alertMixin ],
  data() {
    return {
      channelName: '',
      subdomain: '',
      email: '',
      api_token: '',
      channels: [],
      channelOptions: [
        { id: 'chat', name: 'Chat' },
        { id: 'whatsapp', name: 'WhatsApp' },
        { id: 'native_messaging', name: 'Native Messaging' },
      ],
      users: [],
      excluded_users: [],
      userOptions: [],
      groups: [],
      groupOptions: [],
      tags: [],
      tagsOptions: [],
      minMessages: 10,
      customerMinMessages: 5,
      operatorMinMessages: 5,
      countBotMessages: false,
      showCustomFilters: false,
      showUsers: false,
      showTags: false,
      showGroups: false,
      zendeskLoadingUsers: false,
      zendeskLoadingGroups: false,
      zendeskLoadingTags: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
  },
  watch: {
    showCustomFilters() {
      if (!this.showCustomFilters) this.cleanFilters();
    }
  },
  validations: {
    channelName: { required },
    subdomain: { required, url },
    email: { required },
    api_token: { required },
    minMessages: { numeric },
    customerMinMessages: { numeric },
    operatorMinMessages: { numeric },
  },
  methods: {
    getZendeskData() {
      if (this.email && this.api_token && this.subdomain) {
        this.getGroupsZendesk();
        this.getTagsZendesk();
        this.getUsersZendesk();
      }
    },
    async getUsersZendesk() {
      this.$v.subdomain.$touch();

      try {
        this.zendeskLoadingUsers = true;

        const response = await this.$store.dispatch(
          'inboxes/getZendeskUsers',
          {
            subdomain: this.subdomain,
            email: this.email,
            api_token: this.api_token,
          }
        );
        if (response) {
          this.userOptions = response.data.items;
          this.showUsers = true;
        }
      } catch (error) {
        this.showAlert(
          this.$t('INBOX_MGMT.ADD.ZENDESK_CHANNEL.API.ERROR_MESSAGE')
        );
      }

      this.zendeskLoadingUsers = false;
    },
    async getGroupsZendesk() {
      this.$v.subdomain.$touch();

      try {
        this.zendeskLoadingGroups = true;

        const response = await this.$store.dispatch(
          'inboxes/getZendeskGroups',
          {
            subdomain: this.subdomain,
            email: this.email,
            api_token: this.api_token,
          }
        );
        if (response) {
          this.groupOptions = response.data.items;
          this.showGroups = true;
        }
      } catch (error) {
        this.showAlert(
          this.$t('INBOX_MGMT.ADD.ZENDESK_CHANNEL.API.ERROR_MESSAGE')
        );
      }

      this.zendeskLoadingGroups = false;
    },
    async getTagsZendesk() {
      this.$v.subdomain.$touch();

      try {
        this.zendeskLoadingTags = true;

        const response = await this.$store.dispatch(
          'inboxes/getZendeskTags',
          {
            subdomain: this.subdomain,
            email: this.email,
            api_token: this.api_token,
          }
        );
        if (response) {
          this.tagsOptions = response.data.items;
          this.showTags = true;
        }
      } catch (error) {
        this.showAlert(
          this.$t('INBOX_MGMT.ADD.ZENDESK_CHANNEL.API.ERROR_MESSAGE')
        );
      }

      this.zendeskLoadingTags = false;
    },
    async createChannel() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      try {
        const zendeskChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          {
            name: this.channelName,
            channel: {
              type: 'zendesk',
              identifier: getUuid(),
              subdomain: this.subdomain,
              email: this.email,
              api_token: this.api_token,
              custom_config: {
                min_messages: parseInt(this.minMessages),
                customer_min_messages: parseInt(this.customerMinMessages),
                operator_min_messages: parseInt(this.operatorMinMessages),
                count_bot_messages: this.countBotMessages,
                channels: this.channels.map(channel => ({ id: channel.id, name: channel.name })),
                users: this.users.map(user => ({ id: user.id, name: user.name, email: user.email })),
                excluded_users: this.excluded_users.map(user => ({ id: user.id, name: user.name, email: user.email  })),
                groups: this.groups.map(group => ({ id: group.id, name: group.name })),
                excluded_tags: this.tags.map(tag => ({ count: tag.count, name: tag.name })),
              }
            },
          }
        );

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: zendeskChannel.id,
          },
        });
      } catch (error) {
        this.showAlert(
          this.$t('INBOX_MGMT.ADD.ZENDESK_CHANNEL.API.ERROR_MESSAGE')
        );
      }
    },
    cleanFilters() {
      this.minMessages = 10;
      this.customerMinMessages = 5;
      this.operatorMinMessages = 5;
      this.countBotMessages = false;
    }
  },
};
</script>

<style lang="scss" scoped>
.wizard-body {
  .divider {
    background: #b8b9bb;
    height: 1px;
    margin: 1.5rem 0;
    flex: 0 0 100%;
  }

  .bot-switch {
    .help-text {
      margin-top: 2px;
    }
  }
}
</style>
